import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import sfxEat from "../sfx/eat.mp3";
import sfxGameover from "../sfx/gameover.mp3";
import sfxNewgame from "../sfx/newgame.mp3";

const SFX = Object.freeze({
  newgame: sfxNewgame,
  eat: sfxEat,
  gameover: sfxGameover,
});

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function eq(a, b) {
  return a.x === b.x && a.y === b.y;
}

function spawnFood(snake, cols, rows) {
  const occupied = new Set(snake.map((p) => `${p.x},${p.y}`));
  for (let i = 0; i < 5000; i++) {
    const x = randInt(0, cols - 1);
    const y = randInt(0, rows - 1);
    const key = `${x},${y}`;
    if (!occupied.has(key)) return { x, y };
  }
  // fallback (shouldn't happen)
  return { x: 0, y: 0 };
}

const DIRS = {
  ArrowUp: { x: 0, y: -1 },
  ArrowDown: { x: 0, y: 1 },
  ArrowLeft: { x: -1, y: 0 },
  ArrowRight: { x: 1, y: 0 },
  w: { x: 0, y: -1 },
  s: { x: 0, y: 1 },
  a: { x: -1, y: 0 },
  d: { x: 1, y: 0 },
};

function isOpposite(a, b) {
  return a.x === -b.x && a.y === -b.y;
}

function getDitherPattern(ctx) {
  const c = document.createElement("canvas");
  c.width = 4;
  c.height = 4;
  const p = c.getContext("2d");
  if (!p) return null;

  // 50% grey dither (classic Mac-ish)
  p.fillStyle = "#cfcfcf";
  p.fillRect(0, 0, 4, 4);
  p.fillStyle = "#b5b5b5";
  // checker pattern
  p.fillRect(0, 0, 1, 1);
  p.fillRect(2, 0, 1, 1);
  p.fillRect(1, 1, 1, 1);
  p.fillRect(3, 1, 1, 1);
  p.fillRect(0, 2, 1, 1);
  p.fillRect(2, 2, 1, 1);
  p.fillRect(1, 3, 1, 1);
  p.fillRect(3, 3, 1, 1);

  return ctx.createPattern(c, "repeat");
}

export default function SnakeGame({ isActive }) {
  const cols = 20;
  const rows = 16;
  const tickMs = 95;

  const canvasRef = useRef(null);
  const audioUnlockedRef = useRef(false);
  const audioRef = useRef({ newgame: null, eat: null, gameover: null });
  const patternRef = useRef(null);
  const [started, setStarted] = useState(false);
  const [running, setRunning] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [renderNonce, setRenderNonce] = useState(0);

  const [snake, setSnake] = useState(() => {
    const start = { x: Math.floor(cols / 2), y: Math.floor(rows / 2) };
    return [start, { x: start.x - 1, y: start.y }, { x: start.x - 2, y: start.y }];
  });

  const [dir, setDir] = useState({ x: 1, y: 0 });
  const [queuedDir, setQueuedDir] = useState(null);
  const [food, setFood] = useState(() => spawnFood(snake, cols, rows));
  const score = useMemo(() => Math.max(0, snake.length - 3), [snake.length]);

  const unlockAudio = () => {
    if (audioUnlockedRef.current) return;
    audioUnlockedRef.current = true;
  };

  const playSfx = useCallback((name) => {
    if (!audioUnlockedRef.current) return;
    try {
      let a = audioRef.current[name];
      if (!a) {
        a = new Audio(SFX[name]);
        a.preload = "auto";
        audioRef.current[name] = a;
      }
      a.currentTime = 0;
      const p = a.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } catch {
      // ignore audio failures
    }
  }, []);

  const startNewGame = useCallback(() => {
    const start = { x: Math.floor(cols / 2), y: Math.floor(rows / 2) };
    const nextSnake = [
      start,
      { x: start.x - 1, y: start.y },
      { x: start.x - 2, y: start.y },
    ];
    setSnake(nextSnake);
    setDir({ x: 1, y: 0 });
    setQueuedDir(null);
    setFood(spawnFood(nextSnake, cols, rows));
    setGameOver(false);
    setStarted(true);
    setRunning(true);
    playSfx("newgame");
  }, [cols, rows, playSfx]);

  // Keyboard controls only when window is active.
  useEffect(() => {
    if (!isActive || !started) return;

    function onKeyDown(e) {
      unlockAudio();
      const key = e.key;
      if (key === " " || key === "Spacebar") {
        e.preventDefault();
        if (gameOver) {
          startNewGame();
          return;
        }
        setRunning((v) => !v);
        return;
      }

      if (key === "Enter") {
        if (gameOver) {
          startNewGame();
        }
        return;
      }

      const next = DIRS[key] || DIRS[key?.toLowerCase?.()];
      if (!next) return;
      // Don't reverse into yourself
      if (isOpposite(next, queuedDir || dir)) return;
      setQueuedDir(next);
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isActive, started, dir, queuedDir, gameOver, startNewGame]);

  // Main tick loop.
  useEffect(() => {
    if (!isActive || !started) return;
    if (!running || gameOver) return;

    const id = window.setInterval(() => {
      setSnake((prev) => {
        const nextDir = queuedDir || dir;
        if (queuedDir) setQueuedDir(null);
        setDir(nextDir);

        const head = prev[0];
        const nextHead = { x: head.x + nextDir.x, y: head.y + nextDir.y };

        // wrap like classic variants
        if (nextHead.x < 0) nextHead.x = cols - 1;
        if (nextHead.x >= cols) nextHead.x = 0;
        if (nextHead.y < 0) nextHead.y = rows - 1;
        if (nextHead.y >= rows) nextHead.y = 0;

        // collision with self
        if (prev.some((p) => eq(p, nextHead))) {
          setGameOver(true);
          setRunning(false);
          playSfx("gameover");
          return prev;
        }

        const ate = eq(nextHead, food);
        const next = [nextHead, ...prev];
        if (!ate) next.pop();
        else {
          setFood(spawnFood(next, cols, rows));
          playSfx("eat");
        }
        return next;
      });
    }, tickMs);

    return () => window.clearInterval(id);
  }, [isActive, started, running, gameOver, dir, queuedDir, cols, rows, tickMs, food, playSfx]);

  // Canvas rendering
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Use devicePixelRatio for crisp lines
    const dpr = window.devicePixelRatio || 1;
    const cssW = canvas.clientWidth || 320;
    const cssH = canvas.clientHeight || 240;
    canvas.width = Math.floor(cssW * dpr);
    canvas.height = Math.floor(cssH * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const cell = Math.floor(Math.min(cssW / cols, cssH / rows));
    const boardW = cell * cols;
    const boardH = cell * rows;
    const ox = Math.floor((cssW - boardW) / 2);
    const oy = Math.floor((cssH - boardH) / 2);

    // background (window area)
    ctx.clearRect(0, 0, cssW, cssH);
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, cssW, cssH);

    // classic dithered board background
    if (!patternRef.current) {
      patternRef.current = getDitherPattern(ctx);
    }
    if (patternRef.current) {
      ctx.fillStyle = patternRef.current;
      ctx.fillRect(ox, oy, boardW, boardH);
    } else {
      ctx.fillStyle = "#cfcfcf";
      ctx.fillRect(ox, oy, boardW, boardH);
    }

    // board outline
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2;
    ctx.strokeRect(ox + 1, oy + 1, boardW - 2, boardH - 2);

    // subtle grid
    ctx.strokeStyle = "rgba(0,0,0,0.08)";
    ctx.lineWidth = 1;
    for (let x = 1; x < cols; x++) {
      ctx.beginPath();
      ctx.moveTo(ox + x * cell, oy);
      ctx.lineTo(ox + x * cell, oy + boardH);
      ctx.stroke();
    }
    for (let y = 1; y < rows; y++) {
      ctx.beginPath();
      ctx.moveTo(ox, oy + y * cell);
      ctx.lineTo(ox + boardW, oy + y * cell);
      ctx.stroke();
    }

    // food (accent color)
    ctx.fillStyle = "#af52de";
    ctx.fillRect(ox + food.x * cell + 2, oy + food.y * cell + 2, cell - 4, cell - 4);

    // snake: head accent + body black/white
    snake.forEach((p, idx) => {
      ctx.fillStyle = idx === 0 ? "#0a84ff" : "#000";
      ctx.fillRect(ox + p.x * cell + 2, oy + p.y * cell + 2, cell - 4, cell - 4);
    });

    if (!running && !gameOver) {
      ctx.fillStyle = "rgba(0,0,0,0.75)";
      ctx.fillRect(ox, oy + Math.floor(boardH / 2) - 22, boardW, 44);
      ctx.fillStyle = "#fff";
      ctx.font = "bold 14px Monaco, Geneva, Arial, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("PAUSED — press Space", ox + boardW / 2, oy + boardH / 2);
    }

    if (gameOver) {
      ctx.fillStyle = "rgba(0,0,0,0.82)";
      ctx.fillRect(ox, oy + Math.floor(boardH / 2) - 28, boardW, 56);
      ctx.fillStyle = "#fff";
      ctx.font = "bold 14px Monaco, Geneva, Arial, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("GAME OVER", ox + boardW / 2, oy + boardH / 2 - 10);
      ctx.font = "12px Monaco, Geneva, Arial, sans-serif";
      ctx.fillText("Press Enter to restart", ox + boardW / 2, oy + boardH / 2 + 12);
    }
  }, [snake, food, running, gameOver, cols, rows, isActive, renderNonce]);

  // Re-render on resize of the window/container.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ro = new ResizeObserver(() => {
      setRenderNonce((n) => n + 1);
    });
    ro.observe(canvas);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      className="mac-snake"
      onPointerDown={() => {
        unlockAudio();
      }}
    >
      <div className="mac-snakeHud">
        <div className="mac-snakeTitle">SNAKE</div>
        <div className="mac-snakeMeta">
          <span>Score: {score}</span>
          <span className="mac-snakeDot" aria-hidden="true">
            •
          </span>
          <span>Arrows/WASD</span>
          <span className="mac-snakeDot" aria-hidden="true">
            •
          </span>
          <span>Space: pause</span>
        </div>
      </div>
      <div className="mac-snakeStage">
        <canvas className="mac-snakeCanvas" ref={canvasRef} />
        {!started ? (
          <button
            type="button"
            className="mac-snakeOverlay"
            onClick={() => {
              unlockAudio();
              startNewGame();
            }}
          >
            <div className="mac-snakeOverlayTitle">Click to play</div>
            <div className="mac-snakeOverlaySub">
              Arrows/WASD to move • Space to pause
            </div>
          </button>
        ) : null}
      </div>
    </div>
  );
}

